/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useCallback } from "react";
import { Else, If, Then, When } from "react-if";
import { Spacer, theme } from "@nordcloud/gnui";
import {
  ReservationsAndCommitmentsCoverageOfferingType,
  ReservationsAndCommitmentsCoverageServiceUsageType,
  ReservationsAndCommitmentsFilters,
  ReservationsAndCommitmentsLookbackPeriod,
  ReservationsRecommendationPaymentOption,
  ReservationsRecommendationTerm,
  ReservationsProvider,
} from "~/generated/graphql";
import { Maybe, noop } from "~/tools";
import { RecommendationBanner, SelectableContainer } from "./components";
import {
  useGetCurrentTab,
  useGetRecommendationBoxBannerMessage,
  useGetReservationsDetails,
  useGetReservationsType,
} from "./hooks";
import { ReservationsSectionTitle } from "./ReservationsCommitmentsSectionTitle";
import { ReservationsSummary } from "./ReservationsCommitmentsSummary";
import { ComputeSavingsProps, PaymentOptionsFilter } from "./types";

type Props = {
  value?: Maybe<string>;
  type: ReservationsAndCommitmentsCoverageOfferingType;
  serviceUsageType: ReservationsAndCommitmentsCoverageServiceUsageType;
  timePeriod: ReservationsAndCommitmentsLookbackPeriod;
  selected: boolean;
  filters?: ReservationsAndCommitmentsFilters;
  hasSelectable: boolean;
  paymentOption: ReservationsRecommendationPaymentOption;
  term: ReservationsRecommendationTerm;
  paymentOptionsFilter: PaymentOptionsFilter[];
  provider: ReservationsProvider;
  hasFilters?: boolean;
  onClick: (type: ReservationsAndCommitmentsCoverageOfferingType) => void;
  setComputeSavingsData: (data: ComputeSavingsProps) => void;
  onClearFilters?: () => void;
};

export function ReservationsCommitmentsWidgets({
  type,
  selected = false,
  serviceUsageType,
  timePeriod,
  filters,
  hasSelectable,
  term,
  paymentOption,
  paymentOptionsFilter,
  provider,
  hasFilters = false,
  onClick,
  setComputeSavingsData,
  onClearFilters = noop,
}: Props) {
  const {
    getSummary,
    isSummaryLoading,
    potentialMonthlySaving,
    potentialYearlySaving,
    upfrontCost,
    recurringMonthlyCost,
    savingsRate,
    savingsPlansCommitments,
  } = useGetReservationsDetails({
    serviceUsageType,
    term,
    paymentOption,
    type,
    timePeriod,
    filters,
    provider,
  });

  const isAws = provider === ReservationsProvider.Aws;

  const { label } = useGetReservationsType({
    type,
    paymentOption,
    termOption: term,
    serviceUsageType,
    paymentOptionsFilter,
  });
  const updateSummary = useCallback(() => {
    getSummary();
  }, [getSummary]);

  useEffect(() => {
    updateSummary();
  }, [updateSummary]);

  useEffect(() => {
    if (type === ReservationsAndCommitmentsCoverageOfferingType.SavingsPlans) {
      const data: ComputeSavingsProps = {
        savingsPlansCommitments,
        loading: isSummaryLoading,
      };
      setComputeSavingsData(data);
    }
  }, [savingsPlansCommitments, setComputeSavingsData, isSummaryLoading, type]);

  const onClickBox = () => {
    const data = {
      savingsPlansCommitments,
      loading: isSummaryLoading,
    };
    setComputeSavingsData(data);
    onClick(type);
  };
  const { isComputeTab } = useGetCurrentTab(serviceUsageType);

  const { bannerHeading, bannerText, hasPotentialSavings } =
    useGetRecommendationBoxBannerMessage({
      provider,
      serviceUsageType,
      type,
      potentialMonthlySaving: Number(potentialMonthlySaving),
      hasFilters,
    });

  return (
    <>
      <When condition={!isComputeTab || !isAws}>
        <ReservationsSectionTitle title={label} />
        <Spacer height={theme.spacing.spacing03} />
      </When>
      <SelectableContainer
        selected={selected}
        isSaveSetting={false}
        hasSelectable={hasSelectable}
        onClickBox={onClickBox}
      >
        <div css={{ flex: 1 }}>
          <When condition={isComputeTab && isAws}>
            <ReservationsSectionTitle title={label} />
          </When>
          <If condition={hasPotentialSavings}>
            <Then>
              <ReservationsSummary
                serviceUsageType={serviceUsageType}
                potentialMonthlySaving={potentialMonthlySaving}
                potentialYearlySaving={potentialYearlySaving}
                upfrontCost={upfrontCost}
                recurringMonthlyCost={recurringMonthlyCost}
                savingsRate={savingsRate}
                provider={provider}
              />
            </Then>
            <Else>
              <RecommendationBanner
                heading={bannerHeading}
                subText={bannerText}
                hasFilters={hasFilters}
                onClearFilters={onClearFilters}
              />
            </Else>
          </If>
        </div>
      </SelectableContainer>
    </>
  );
}
